import React, { useState, useEffect, useContext, useCallback, useRef, useImperativeHandle, forwardRef } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { API_BASE_URL } from "../../../environment";
import { AuthContext } from "../../../context/authcontext";

interface ProductService {
  product_n_service_id: number;
  product_n_service_name: string;
  product_n_service_unit_price: number;
  currency_code: string;
  product_n_service_description?: string;
}

interface ProductServiceItem {
  id: number;
  product_n_service_id: number | null;
  product_n_service_name?: string;
  product_n_service_unit_price?: number | string;
  original_unit_price?: number | string;
  quantity: number;
  discount_percentage: number;
  gross_total?: number;
  discounted_total?: number;
  lifecycle_product_n_service_id?: number;
  currency_code?: string;
  isExisting?: boolean;
  service_tax_percentage?: number;
  gst_tax_percentage?: number;
  vat_tax_percentage?: number;
  service_tax_total?: number;
  gst_tax_total?: number;
  vat_tax_total?: number;
  product_n_service_description?: string;
  override_product_n_service_description?: string | null;
  override_product_n_service_unit_price?: number | string | null;
}

interface ProductsServicesSelectorProps {
  value?: ProductServiceItem[];
  onChange?: (items: ProductServiceItem[]) => void;
  existingItems?: any[];
  isEditMode?: boolean;
  isDetailPage?: boolean;
  entityId?: number;
  entityType?: string;
  onRefreshData?: () => void;
  onUnsavedChanges?: (hasUnsaved: boolean) => void;
  currencyCode?: string;
  tenantInfo?: {
    is_service_tax?: boolean;
    is_gst_tax?: boolean;
    is_vat_tax?: boolean;
    service_tax_default_percentage?: number;
    gst_tax_default_percentage?: number;
    vat_tax_default_percentage?: number;
    proposal_max_discount_percentage?: number;
  };
  onDiscountErrors?: (hasErrors: boolean) => void;
  onSaveAllChanges?: () => Promise<void>;
}

export interface ProductsServicesSelectorRef {
  saveAllChanges: () => Promise<void>;
}

const ProductsServicesSelector = forwardRef<ProductsServicesSelectorRef, ProductsServicesSelectorProps>(({
  value = [],
  onChange,
  existingItems = [],
  isEditMode = false,
  isDetailPage,
  entityId,
  entityType,
  onRefreshData,
  onUnsavedChanges,
  currencyCode,
  tenantInfo,
  onDiscountErrors,
  onSaveAllChanges,
}, ref) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [items, setItems] = useState<ProductServiceItem[]>([]);
  const [productServices, setProductServices] = useState<ProductService[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [originalItems, setOriginalItems] = useState<ProductServiceItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastExistingItemsLength, setLastExistingItemsLength] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [discountErrors, setDiscountErrors] = useState<{ [id: number]: string }>({});

  const MySwal = withReactContent(Swal);

  const getDiscountError = (discountPercentage: number) => {
    const maxDiscount = Number(tenantInfo?.proposal_max_discount_percentage ?? 0);
    const hasMaxDiscountLimit = Number.isFinite(maxDiscount) && maxDiscount > 0;

    if (discountPercentage > 100) {
      return "Cannot exceed 100%";
    }

    if (hasMaxDiscountLimit && discountPercentage > maxDiscount) {
      return `Max allowed: ${maxDiscount}%`;
    }

    return "";
  };

  const getDiscountErrors = (itemsToValidate: ProductServiceItem[]) => {
    return itemsToValidate.reduce<{ [id: number]: string }>((errors, item) => {
      const errorMsg = getDiscountError(item.discount_percentage || 0);
      if (errorMsg) {
        errors[item.id] = errorMsg;
      }
      return errors;
    }, {});
  };

  const saveAllChanges = async () => {
    if (!entityId || !entityType) return;

    const currentDiscountErrors = getDiscountErrors(items);
    if (Object.keys(currentDiscountErrors).length > 0) {
      setDiscountErrors(currentDiscountErrors);
      onDiscountErrors?.(true);
      MySwal.fire({
        icon: "warning",
        title: "Invalid Discount",
        text: "Please fix the discount percentage errors before saving.",
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    const itemsToSave = items.filter(item =>
      item.product_n_service_id &&
      (!item.isExisting || hasItemChanged(item))
    );

    if (itemsToSave.length === 0) {
      MySwal.fire({
        icon: "info",
        title: "No Changes",
        text: "No changes to save.",
        timer: 2000,
        showConfirmButton: false,
      });
      return;
    }

    try {
      let allSuccess = true;
      for (const item of itemsToSave) {
        const success = await handleSaveItem(item.id);
        if (!success) {
          allSuccess = false;
        }
      }

      if (allSuccess) {
        MySwal.fire({
          icon: "success",
          title: "Success!",
          text: `${itemsToSave.length} product${itemsToSave.length > 1 ? 's' : ''}/service${itemsToSave.length > 1 ? 's' : ''} saved successfully.`,
          timer: 3000,
          showConfirmButton: false,
        });

        if (onRefreshData) {
          onRefreshData();
          setTimeout(() => onRefreshData(), 500);
        }
      }
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to save some products/services. Please try again.",
      });
    }
  };

  useImperativeHandle(ref, () => ({
    saveAllChanges
  }));

  useEffect(() => {
    const isNewLead =
      (existingItems.length === 0 && lastExistingItemsLength > 0) ||
      (existingItems.length > 0 && lastExistingItemsLength === 0);

    if (!isInitialized || isNewLead) {
      if (existingItems.length > 0) {
        const mappedItems = existingItems.map((item, index) => ({
          id: index + 1,
          product_n_service_id: item.product_n_service_id,
          product_n_service_name: item.product_n_service_name,
          product_n_service_unit_price: item.override_product_n_service_unit_price ?? item.product_n_service_unit_price,
          original_unit_price: item.product_n_service_unit_price,
          quantity: item.quantity || 1,
          discount_percentage: item.discount_percentage || 0,
          gross_total: item.gross_total || 0,
          discounted_total: item.discounted_total || 0,
          lifecycle_product_n_service_id: item.lifecycle_product_n_service_id,
          currency_code: item.currency_code,
          isExisting: true,
          product_n_service_description: item.product_n_service_description,
          override_product_n_service_description: item.override_product_n_service_description ?? item.product_n_service_description ?? null,
          override_product_n_service_unit_price: item.override_product_n_service_unit_price ?? item.product_n_service_unit_price,
          service_tax_percentage: item.service_tax_percentage !== undefined ? item.service_tax_percentage : (tenantInfo?.service_tax_default_percentage ?? 0),
          gst_tax_percentage: item.gst_tax_percentage !== undefined ? item.gst_tax_percentage : (tenantInfo?.gst_tax_default_percentage ?? 0),
          vat_tax_percentage: item.vat_tax_percentage !== undefined ? item.vat_tax_percentage : (tenantInfo?.vat_tax_default_percentage ?? 0),
          service_tax_total: item.service_tax_total || 0,
          gst_tax_total: item.gst_tax_total || 0,
          vat_tax_total: item.vat_tax_total || 0,
        }));
        setItems(mappedItems);
        setOriginalItems(JSON.parse(JSON.stringify(mappedItems)));
      } else if (value.length > 0) {
        setItems(value);
      } else {
        setItems([
          {
            id: 1,
            product_n_service_id: null,
            quantity: 1,
            discount_percentage: 0,
            isExisting: false,
            service_tax_percentage: tenantInfo?.is_service_tax ? (tenantInfo?.service_tax_default_percentage ?? 0) : 0,
            gst_tax_percentage: tenantInfo?.is_gst_tax ? (tenantInfo?.gst_tax_default_percentage ?? 0) : 0,
            vat_tax_percentage: tenantInfo?.is_vat_tax ? (tenantInfo?.vat_tax_default_percentage ?? 0) : 0,
            service_tax_total: 0,
            gst_tax_total: 0,
            vat_tax_total: 0,
          },
        ]);
      }
      setIsInitialized(true);
      setLastExistingItemsLength(existingItems.length);
    }
  }, [existingItems, value, isInitialized, lastExistingItemsLength]);

  useEffect(() => {
    if (existingItems.length > 0 && isInitialized) {
      const hasExistingItemsChanged =
        JSON.stringify(existingItems) !==
        JSON.stringify(
          originalItems.map((item) => ({
            product_n_service_id: item.product_n_service_id,
            product_n_service_name: item.product_n_service_name,
            product_n_service_unit_price: item.product_n_service_unit_price,
            quantity: item.quantity,
            discount_percentage: item.discount_percentage,
            gross_total: item.gross_total,
            discounted_total: item.discounted_total,
            lifecycle_product_n_service_id: item.lifecycle_product_n_service_id,
          }))
        );

      if (hasExistingItemsChanged) {
        const mappedItems = existingItems.map((item, index) => ({
          id: index + 1,
          product_n_service_id: item.product_n_service_id,
          product_n_service_name: item.product_n_service_name,
          product_n_service_unit_price: item.override_product_n_service_unit_price ?? item.product_n_service_unit_price,
          original_unit_price: item.product_n_service_unit_price,
          quantity: item.quantity || 1,
          discount_percentage: item.discount_percentage || 0,
          gross_total: item.gross_total || 0,
          discounted_total: item.discounted_total || 0,
          lifecycle_product_n_service_id: item.lifecycle_product_n_service_id,
          currency_code: item.currency_code,
          isExisting: true,
          product_n_service_description: item.product_n_service_description,
          override_product_n_service_description: item.override_product_n_service_description ?? item.product_n_service_description ?? null,
          override_product_n_service_unit_price: item.override_product_n_service_unit_price ?? item.product_n_service_unit_price,
          service_tax_percentage: item.service_tax_percentage !== undefined ? item.service_tax_percentage : (tenantInfo?.service_tax_default_percentage ?? 0),
          gst_tax_percentage: item.gst_tax_percentage !== undefined ? item.gst_tax_percentage : (tenantInfo?.gst_tax_default_percentage ?? 0),
          vat_tax_percentage: item.vat_tax_percentage !== undefined ? item.vat_tax_percentage : (tenantInfo?.vat_tax_default_percentage ?? 0),
          service_tax_total: item.service_tax_total || 0,
          gst_tax_total: item.gst_tax_total || 0,
          vat_tax_total: item.vat_tax_total || 0,
        }));
        setItems(mappedItems);
        setOriginalItems(JSON.parse(JSON.stringify(mappedItems)));
      }
    }
  }, [existingItems]);

  const debouncedSearch = useCallback((searchValue: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchValue);
      fetchProductServices(1, false, searchValue);
    }, 500);
  }, [selectedTenantId]);

  const handleInputChange = useCallback((inputValue: string) => {
    if (inputValue !== searchTerm) {
      debouncedSearch(inputValue);
    }
  }, [debouncedSearch, searchTerm]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currencyCode) {
      setProductServices([]);
      setPage(1);
      setHasMore(true);
      fetchProductServices(1, false, searchTerm);
    }
  }, [currencyCode]);

  const fetchProductServices = async (pageNum = 1, append = false, search = "") => {
    if (!selectedTenantId) return;

    const isSearchRequest = search !== "";
    if (isSearchRequest) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }

    try {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return;

      let url = `${API_BASE_URL}/product-n-service/?tenant_id=${selectedTenantId}&page=${pageNum}&is_active=true&currency_code=${currencyCode}&response_mode=light`;
      if (search) {
        url += `&product_n_service_name=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch products/services");

      const data = await response.json();
      const services = Array.isArray(data.results) ? data.results : [];

      if (append && !isSearchRequest) {
        setProductServices((prev) => [...prev, ...services]);
      } else {
        setProductServices(services);
        if (isSearchRequest) {
          setPage(1);
        }
      }

      setHasMore(!!data.next);
      if (!isSearchRequest) {
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Error fetching products/services:", error);
    } finally {
      if (isSearchRequest) {
        setIsSearching(false);
      } else {
        setLoading(false);
      }
    }
  };

  const loadMoreOptions = () => {
    if (hasMore && !loading && !isSearching && searchTerm === "") {
      fetchProductServices(page + 1, true);
    }
  };

  const hasItemChanged = (item: ProductServiceItem) => {
    if (!item.isExisting) return false;
    const original = originalItems.find((orig) => orig.id === item.id);
    if (!original) return false;

    return (
      original.quantity !== item.quantity ||
      original.discount_percentage !== item.discount_percentage ||
      original.product_n_service_id !== item.product_n_service_id ||
      original.service_tax_percentage !== item.service_tax_percentage ||
      original.gst_tax_percentage !== item.gst_tax_percentage ||
      original.vat_tax_percentage !== item.vat_tax_percentage ||
      original.override_product_n_service_description !== item.override_product_n_service_description ||
      original.override_product_n_service_unit_price !== item.override_product_n_service_unit_price
    );
  };

  const checkForUnsavedChanges = () => {
    const hasUnsaved = items.some(
      (item) => item.isExisting && hasItemChanged(item)
    );
    if (onUnsavedChanges) {
      onUnsavedChanges(hasUnsaved);
    }
    return hasUnsaved;
  };

  useEffect(() => {
    if (isInitialized) {
      checkForUnsavedChanges();
    }
  }, [items, originalItems, isInitialized]);

  useEffect(() => {
    const nextDiscountErrors = getDiscountErrors(items);
    setDiscountErrors(nextDiscountErrors);
    onDiscountErrors?.(Object.keys(nextDiscountErrors).length > 0);
  }, [items, tenantInfo?.proposal_max_discount_percentage]);

  const productServiceOptions = productServices.map((service) => ({
    value: service.product_n_service_id,
    label: `${service.product_n_service_name} - ${service.currency_code} ${service.product_n_service_unit_price}`,
    service,
  }));

  const calculateTotals = (item: ProductServiceItem) => {
    if (!item.product_n_service_unit_price || !item.quantity) {
      return {
        gross_total: 0,
        discounted_total: 0,
        service_tax_total: 0,
        gst_tax_total: 0,
        vat_tax_total: 0
      };
    }

    const gross_total = Number(item.product_n_service_unit_price) * item.quantity;
    const discount_amount = (gross_total * (item.discount_percentage || 0)) / 100;
    const discounted_total = gross_total - discount_amount;

    let service_tax_total = 0;
    let gst_tax_total = 0;
    let vat_tax_total = 0;

    if ((item.service_tax_percentage || 0) > 0) {
      service_tax_total = (discounted_total * (item.service_tax_percentage || 0)) / 100;
    }

    if ((item.gst_tax_percentage || 0) > 0) {
      const baseForGST = discounted_total + service_tax_total;
      gst_tax_total = (baseForGST * (item.gst_tax_percentage || 0)) / 100;
    }

    if ((item.vat_tax_percentage || 0) > 0) {
      const baseForVAT = discounted_total + service_tax_total + gst_tax_total;
      vat_tax_total = (baseForVAT * (item.vat_tax_percentage || 0)) / 100;
    }

    return {
      gross_total,
      discounted_total,
      service_tax_total,
      gst_tax_total,
      vat_tax_total
    };
  };

  const handleItemChange = (
    id: number,
    field: keyof ProductServiceItem,
    value: any
  ) => {
    const updatedItems = items.map((item) => {
      if (item.id === id) {
        let updatedItem = { ...item, [field]: value };

        if (field === "product_n_service_id") {
          const selectedService = productServices.find(
            (s) => s.product_n_service_id === value
          );
          if (selectedService) {
            updatedItem = {
              ...updatedItem,
              product_n_service_name: selectedService.product_n_service_name,
              product_n_service_unit_price: selectedService.product_n_service_unit_price,
              original_unit_price: selectedService.product_n_service_unit_price,
              currency_code: selectedService.currency_code,
              product_n_service_description: selectedService.product_n_service_description,
              override_product_n_service_description: null,
              override_product_n_service_unit_price: selectedService.product_n_service_unit_price,
            };
          }
        }

        if (field === "product_n_service_unit_price") {
          updatedItem = {
            ...updatedItem,
            override_product_n_service_unit_price: value,
          };
        }

        if (
          field === "quantity" ||
          field === "discount_percentage" ||
          field === "product_n_service_id" ||
          field === "product_n_service_unit_price" ||
          field === "service_tax_percentage" ||
          field === "gst_tax_percentage" ||
          field === "vat_tax_percentage"
        ) {
          const totals = calculateTotals(updatedItem);
          updatedItem = { ...updatedItem, ...totals };
        }

        return updatedItem;
      }
      return item;
    });

    setItems(updatedItems);
    onChange?.(updatedItems);
  };

  const handleSaveItem = async (id: number) => {
    const item = items.find((item) => item.id === id);
    if (!item || !item.product_n_service_id) return false;

    const unitPriceToSave = Number(
      item.override_product_n_service_unit_price !== null &&
      item.override_product_n_service_unit_price !== undefined &&
      !isNaN(Number(item.override_product_n_service_unit_price))
        ? item.override_product_n_service_unit_price
        : (item.product_n_service_unit_price ?? item.original_unit_price ?? 0)
    );

    const descriptionToSave =
      item.override_product_n_service_description !== null &&
      item.override_product_n_service_description !== undefined
        ? item.override_product_n_service_description
        : (item.product_n_service_description ?? "");

    try {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return false;

      if (item.isExisting && item.lifecycle_product_n_service_id) {
        const originalItem = originalItems.find((orig) => orig.id === id);
        const hasProductChanged = originalItem && originalItem.product_n_service_id !== item.product_n_service_id;

        if (hasProductChanged) {
          const deleteResponse = await fetch(
            `${API_BASE_URL}/lifecycle-product-n-service/${item.lifecycle_product_n_service_id}/`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authHeader}`,
              },
            }
          );

          if (deleteResponse.ok) {
            const createResponse = await fetch(
              `${API_BASE_URL}/lifecycle-product-n-service/`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Basic ${authHeader}`,
                },
                body: JSON.stringify({
                  product_n_service_id: item.product_n_service_id,
                  quantity: item.quantity,
                  discount_percentage: item.discount_percentage,
                  gross_total: item.gross_total,
                  discounted_total: item.discounted_total,
                  related_entity_name: entityType,
                  related_entity_id: entityId,
                  tenant_id: selectedTenantId ? parseInt(selectedTenantId, 10) : null,
                  service_tax_percentage: item.service_tax_percentage || 0,
                  gst_tax_percentage: item.gst_tax_percentage || 0,
                  vat_tax_percentage: item.vat_tax_percentage || 0,
                  service_tax_total: item.service_tax_total || 0,
                  gst_tax_total: item.gst_tax_total || 0,
                  vat_tax_total: item.vat_tax_total || 0,
                  override_product_n_service_description: descriptionToSave,
                  override_product_n_service_unit_price: unitPriceToSave,
                }),
              }
            );

            if (createResponse.ok) {
              const responseData = await createResponse.json();
              setItems((prevItems) =>
                prevItems.map((itm) =>
                  itm.id === id
                    ? {
                      ...itm,
                      lifecycle_product_n_service_id:
                        responseData.lifecycle_product_n_service_id,
                      isExisting: true,
                    }
                    : itm
                )
              );
              setOriginalItems((prevOrig) =>
                prevOrig.map((orig) =>
                  orig.id === id
                    ? {
                      ...item,
                      lifecycle_product_n_service_id:
                        responseData.lifecycle_product_n_service_id,
                      isExisting: true,
                    }
                    : orig
                )
              );
              return true;
            } else {
              const errData = await createResponse.json().catch(() => null);
              const errMsg = errData
                ? Object.entries(errData)
                    .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                    .join("\n")
                : "Failed to update product/service.";
              MySwal.fire({
                icon: "error",
                title: "Error!",
                text: errMsg,
              });
              return false;
            }
          } else {
            const errData = await deleteResponse.json().catch(() => null);
            const errMsg = errData
              ? Object.entries(errData)
                  .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                  .join("\n")
              : "Failed to update product/service.";
            MySwal.fire({
              icon: "error",
              title: "Error!",
              text: errMsg,
            });
            return false;
          }
        } else {
          const response = await fetch(
            `${API_BASE_URL}/lifecycle-product-n-service/${item.lifecycle_product_n_service_id}/`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authHeader}`,
              },
              body: JSON.stringify({
                quantity: item.quantity,
                discount_percentage: item.discount_percentage,
                gross_total: item.gross_total,
                discounted_total: item.discounted_total,
                service_tax_percentage: item.service_tax_percentage || 0,
                gst_tax_percentage: item.gst_tax_percentage || 0,
                vat_tax_percentage: item.vat_tax_percentage || 0,
                service_tax_total: item.service_tax_total || 0,
                gst_tax_total: item.gst_tax_total || 0,
                vat_tax_total: item.vat_tax_total || 0,
                override_product_n_service_description: descriptionToSave,
                override_product_n_service_unit_price: unitPriceToSave,
              }),
            }
          );

          if (response.ok) {
            setOriginalItems((prevOrig) =>
              prevOrig.map((orig) => (orig.id === id ? { ...item } : orig))
            );
            return true;
          } else {
            const errData = await response.json().catch(() => null);
            const errMsg = errData
              ? Object.entries(errData)
                  .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                  .join("\n")
              : "Failed to update product/service.";
            MySwal.fire({
              icon: "error",
              title: "Error!",
              text: errMsg,
            });
            return false;
          }
        }
      } else {
        const response = await fetch(
          `${API_BASE_URL}/lifecycle-product-n-service/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
            body: JSON.stringify({
              product_n_service_id: item.product_n_service_id,
              quantity: item.quantity,
              discount_percentage: item.discount_percentage,
              gross_total: item.gross_total,
              discounted_total: item.discounted_total,
              related_entity_name: entityType,
              related_entity_id: entityId,
              tenant_id: selectedTenantId ? parseInt(selectedTenantId, 10) : null,
              service_tax_percentage: item.service_tax_percentage || 0,
              gst_tax_percentage: item.gst_tax_percentage || 0,
              vat_tax_percentage: item.vat_tax_percentage || 0,
              service_tax_total: item.service_tax_total || 0,
              gst_tax_total: item.gst_tax_total || 0,
              vat_tax_total: item.vat_tax_total || 0,
              override_product_n_service_description: descriptionToSave,
              override_product_n_service_unit_price: unitPriceToSave,
            }),
          }
        );

        if (response.ok) {
          const responseData = await response.json();

          setItems((prevItems) =>
            prevItems.map((itm) =>
              itm.id === id
                ? {
                  ...itm,
                  lifecycle_product_n_service_id:
                    responseData.lifecycle_product_n_service_id,
                  isExisting: true,
                }
                : itm
            )
          );

          setOriginalItems((prevOrig) => [
            ...prevOrig.filter((orig) => orig.id !== id),
            {
              ...item,
              lifecycle_product_n_service_id:
                responseData.lifecycle_product_n_service_id,
              isExisting: true,
            },
          ]);
          return true;
        } else {
          const errData = await response.json().catch(() => null);
          const errMsg = errData
            ? Object.entries(errData)
                .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(", ") : val}`)
                .join("\n")
            : "Failed to add product/service.";
          MySwal.fire({
            icon: "error",
            title: "Error!",
            text: errMsg,
          });
          return false;
        }
      }
    } catch (error) {
      console.error("Error saving product/service:", error);
      return false;
    }
  };

  const handleDeleteClick = async (id: number) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this action!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      await handleConfirmDelete(id);
    }
  };

  const handleConfirmDelete = async (id: number) => {
    const item = items.find((item) => item.id === id);

    if (item?.isExisting && item.lifecycle_product_n_service_id) {
      try {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return;

        const response = await fetch(
          `${API_BASE_URL}/lifecycle-product-n-service/${item.lifecycle_product_n_service_id}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (response.ok) {
          MySwal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Product/Service has been deleted successfully.",
            timer: 2000,
            showConfirmButton: false,
          });

          if (onRefreshData) {
            onRefreshData();
            setTimeout(() => onRefreshData(), 500);
          }
        } else {
          MySwal.fire({
            icon: "error",
            title: "Error!",
            text: "Failed to delete product/service.",
          });
          return;
        }
      } catch (error) {
        console.error("Error deleting product/service:", error);
        MySwal.fire({
          icon: "error",
          title: "Error!",
          text: "An error occurred while deleting the product/service.",
        });
        return;
      }
    }

    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);
    onChange?.(updatedItems);
    setDiscountErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      onDiscountErrors?.(Object.keys(next).length > 0);
      return next;
    });
  };

  const handleAddMore = () => {
    const maxId =
      items.length > 0 ? Math.max(...items.map((item) => item.id)) : 0;
    const newId = maxId + 1;
    const newItem: ProductServiceItem = {
      id: newId,
      product_n_service_id: null,
      quantity: 1,
      discount_percentage: 0,
      gross_total: 0,
      discounted_total: 0,
      isExisting: false,
      service_tax_percentage: tenantInfo?.is_service_tax ? (tenantInfo?.service_tax_default_percentage ?? 0) : 0,
      gst_tax_percentage: tenantInfo?.is_gst_tax ? (tenantInfo?.gst_tax_default_percentage ?? 0) : 0,
      vat_tax_percentage: tenantInfo?.is_vat_tax ? (tenantInfo?.vat_tax_default_percentage ?? 0) : 0,
      service_tax_total: 0,
      gst_tax_total: 0,
      vat_tax_total: 0,
    };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onChange?.(updatedItems);
  };

  const renderProductServiceItem = (item: ProductServiceItem) => {
    let currencyCode = item.currency_code;

    if (!currencyCode && item.product_n_service_id) {
      const selectedService = productServices.find(
        (s) => s.product_n_service_id === item.product_n_service_id
      );
      currencyCode = selectedService?.currency_code || "USD";
    } else {
      currencyCode = currencyCode || "USD";
    }
    const unitPrice = item.override_product_n_service_unit_price ?? item.product_n_service_unit_price ?? "";
    const grossTotal = item.gross_total || 0;
    const discountedTotal = item.discounted_total || 0;
    const serviceTaxTotal = item.service_tax_total || 0;
    const gstTaxTotal = item.gst_tax_total || 0;
    const vatTaxTotal = item.vat_tax_total || 0;
    const finalTotal = discountedTotal + serviceTaxTotal + gstTaxTotal + vatTaxTotal;
    const originalItem = originalItems.find(orig => orig.id === item.id);
    const hasExistingTaxValues = item.isExisting && (
      (originalItem?.service_tax_percentage !== undefined && originalItem.service_tax_percentage > 0) ||
      (originalItem?.gst_tax_percentage !== undefined && originalItem.gst_tax_percentage > 0) ||
      (originalItem?.vat_tax_percentage !== undefined && originalItem.vat_tax_percentage > 0)
    );
    const hasEnabledTaxFields = tenantInfo?.is_service_tax || tenantInfo?.is_gst_tax || tenantInfo?.is_vat_tax;
    const showTaxFields = (hasEnabledTaxFields || hasExistingTaxValues) && (entityType === "deal" || entityType === "proposal" || entityType === "contract");

    return (
      <div key={item.id} className="card mb-3" style={{ border: "1px solid #e9ecef", backgroundColor: "#f8f9fa" }}>
        <div className="card-body p-3">
          <div className="row g-2">
            <div className="col-md-6 col-10">
              <label className="form-label small fw-bold">Product / Service</label>
              <Select
                options={productServiceOptions}
                value={
                  item.isExisting && item.product_n_service_name
                    ? {
                      value: item.product_n_service_id,
                      label: `${item.product_n_service_name}`,
                    }
                    : productServiceOptions.find(
                      (option) => option.value === item.product_n_service_id
                    )
                }
                onChange={(option) =>
                  handleItemChange(
                    item.id,
                    "product_n_service_id",
                    option?.value || null
                  )
                }
                onInputChange={handleInputChange}
                placeholder="Select Product / Service"
                isClearable
                isLoading={loading || isSearching}
                onMenuScrollToBottom={loadMoreOptions}
                noOptionsMessage={() => {
                  if (loading || isSearching) return "Searching...";
                  if (searchTerm) return "No products/services found for your search";
                  return "No products/services found";
                }}
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "32px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "12px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#6c757d",
                    fontSize: "12px",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div className="col-md-2 col-6">
              <label className="form-label small fw-bold">Unit Price</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: "12px" }}>{currencyCode}</span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={unitPrice}
                  onChange={(e) => {
                    const value = e.target.value;
                    const val = value === "" || isNaN(parseFloat(value)) ? "" : parseFloat(value);
                    handleItemChange(item.id, "product_n_service_unit_price", val);
                  }}
                  min="0"
                  step="any"
                  style={{ fontSize: "12px", height: "36px" }}
                />
              </div>
            </div>

            <div className="col-md-2 col-6">
              <label className="form-label small fw-bold">Qty</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={item.quantity || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = value === "" ? "" : parseInt(value) || 1;
                  handleItemChange(item.id, "quantity", numValue);
                }}
                placeholder="Qty"
                min="1"
                style={{ fontSize: "12px", height: "36px" }}
              />
            </div>

            <div className="col-md-2 col-6">
              <label className="form-label small fw-bold">Total</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={`${currencyCode} ${grossTotal.toFixed(4)}`}
                readOnly
                style={{
                  fontSize: "12px",
                  backgroundColor: "#f8f9fa",
                  color: "#6c757d",
                  height: "36px",
                }}
              />
            </div>

            <div className="col-md-2 col-6">
              <label className="form-label small fw-bold">Disc %</label>
              <input
                type="number"
                className={`form-control form-control-sm ${discountErrors[item.id] ? "is-invalid" : ""}`}
                value={item.discount_percentage || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    handleItemChange(item.id, "discount_percentage", 0);
                    setDiscountErrors((prev) => {
                      const next = { ...prev };
                      delete next[item.id];
                      onDiscountErrors?.(Object.keys(next).length > 0);
                      return next;
                    });
                    return;
                  }
                  const numValue = parseFloat(value) || 0;
                  const errorMsg = getDiscountError(numValue);

                  setDiscountErrors((prev) => {
                    const next = { ...prev };
                    if (errorMsg) {
                      next[item.id] = errorMsg;
                    } else {
                      delete next[item.id];
                    }
                    onDiscountErrors?.(Object.keys(next).length > 0);
                    return next;
                  });

                  handleItemChange(item.id, "discount_percentage", numValue);
                }}
                placeholder="Disc %"
                min="0"
                max="100"
                step="1"
                style={{ fontSize: "12px", height: "36px" }}
              />
              {discountErrors[item.id] && (
                <div className="invalid-feedback" style={{ fontSize: "10px" }}>
                  {discountErrors[item.id]}
                </div>
              )}
            </div>

            <div className="col-md-2 col-6">
              <label className="form-label small fw-bold">Final Price</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={`${currencyCode} ${discountedTotal.toFixed(4)}`}
                readOnly
                style={{
                  fontSize: "12px",
                  backgroundColor: "#f8f9fa",
                  color: "#6c757d",
                  height: "36px",
                }}
              />
            </div>

            {showTaxFields && (
              <>
                {(tenantInfo?.is_service_tax || (item.isExisting && originalItem?.service_tax_percentage !== undefined && originalItem.service_tax_percentage > 0)) && (
                  <div className="col-md-2 col-6">
                    <label className="form-label small fw-bold">Service Tax %</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.service_tax_percentage !== undefined ? item.service_tax_percentage : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleItemChange(item.id, "service_tax_percentage", 0);
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (isNaN(numValue) || numValue < 0) {
                          return;
                        }
                        if (numValue > 100) {
                          MySwal.fire({
                            icon: "warning",
                            title: "Invalid Percentage",
                            text: "Service tax percentage cannot exceed 100%",
                            timer: 3000,
                            showConfirmButton: false,
                          });
                          return;
                        }
                        handleItemChange(item.id, "service_tax_percentage", numValue);
                      }}
                      placeholder="Service Tax %"
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ fontSize: "12px", height: "36px" }}
                    />
                  </div>
                )}

                {(tenantInfo?.is_gst_tax || (item.isExisting && originalItem?.gst_tax_percentage !== undefined && originalItem.gst_tax_percentage > 0)) && (
                  <div className="col-md-2 col-6">
                    <label className="form-label small fw-bold">GST %</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.gst_tax_percentage !== undefined ? item.gst_tax_percentage : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleItemChange(item.id, "gst_tax_percentage", 0);
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (isNaN(numValue) || numValue < 0) {
                          return;
                        }
                        if (numValue > 100) {
                          MySwal.fire({
                            icon: "warning",
                            title: "Invalid Percentage",
                            text: "GST percentage cannot exceed 100%",
                            timer: 3000,
                            showConfirmButton: false,
                          });
                          return;
                        }
                        handleItemChange(item.id, "gst_tax_percentage", numValue);
                      }}
                      placeholder="GST %"
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ fontSize: "12px", height: "36px" }}
                    />
                  </div>
                )}

                {(tenantInfo?.is_vat_tax || (item.isExisting && originalItem?.vat_tax_percentage !== undefined && originalItem.vat_tax_percentage > 0)) && (
                  <div className="col-md-2 col-6">
                    <label className="form-label small fw-bold">VAT %</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.vat_tax_percentage !== undefined ? item.vat_tax_percentage : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleItemChange(item.id, "vat_tax_percentage", 0);
                          return;
                        }
                        const numValue = parseFloat(value);
                        if (isNaN(numValue) || numValue < 0) {
                          return;
                        }
                        if (numValue > 100) {
                          MySwal.fire({
                            icon: "warning",
                            title: "Invalid Percentage",
                            text: "VAT percentage cannot exceed 100%",
                            timer: 3000,
                            showConfirmButton: false,
                          });
                          return;
                        }
                        handleItemChange(item.id, "vat_tax_percentage", numValue);
                      }}
                      placeholder="VAT %"
                      min="0"
                      max="100"
                      step="0.01"
                      style={{ fontSize: "12px", height: "36px" }}
                    />
                  </div>
                )}

                <div className="col-md-2 col-6">
                  <label className="form-label small fw-bold">Final Price</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={`${currencyCode} ${finalTotal.toFixed(4)}`}
                    readOnly
                    style={{
                      fontSize: "12px",
                      backgroundColor: "#f8f9fa",
                      color: "#6c757d",
                      fontWeight: "bold",
                      height: "36px",
                    }}
                  />
                </div>
              </>
            )}

            <div className="col-12 mt-1">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <label className="form-label small fw-bold mb-0">Description</label>
                {item.override_product_n_service_description !== null && item.product_n_service_description && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary d-flex align-items-center"
                    onClick={() => handleItemChange(item.id, "override_product_n_service_description", null)}
                    title="Reset to default description"
                    style={{ fontSize: "10px", padding: "2px 6px" }}
                  >
                    <svg width="11" height="11" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                      <path fillRule="evenodd" d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z" />
                      <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z" />
                    </svg>
                    Reset
                  </button>
                )}
              </div>
              <textarea
                className="form-control form-control-sm"
                value={item.override_product_n_service_description ?? item.product_n_service_description ?? ""}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) {
                    handleItemChange(item.id, "override_product_n_service_description", e.target.value);
                  }
                }}
                rows={3}
                maxLength={1000}
                placeholder="No description"
                style={{ fontSize: "12px", resize: "vertical", minHeight: "72px" }}
              />
              <small className="text-muted">
                {(item.override_product_n_service_description ?? item.product_n_service_description ?? "").length}/1000
              </small>
            </div>

            <div className="col-12 d-flex justify-content-end gap-2 mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-danger d-flex align-items-center"
                onClick={() => handleDeleteClick(item.id)}
                title="Delete item"
                style={{
                  fontSize: "11px",
                  padding: "4px 8px",
                }}
              >
                <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16" className="me-1">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                  <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!currencyCode) {
    return (
      <div className="alert alert-info d-flex align-items-center">
        <i className="fas fa-info-circle me-2"></i>
        Please select a currency code before adding products/services.
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-body">
          {items.map((item) => renderProductServiceItem(item))}

          <div className="mt-3 d-flex justify-content-between align-items-center">
            <button
              type="button"
              className="btn btn-outline-primary d-flex align-items-center"
              onClick={handleAddMore}
              style={{
                border: "2px dashed #dee2e6",
                borderRadius: "6px",
                backgroundColor: "transparent",
                color: "#6c757d",
                fontSize: "12px",
                fontWeight: "bold",
                padding: "8px 16px",
              }}
            >
              <i className="ti ti-square-rounded-plus me-2" />
              Add More
            </button>

            {isDetailPage && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={saveAllChanges}
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "8px 16px",
                }}
              >
                <i className="ti ti-device-floppy me-2" />
                Save All Changes
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

ProductsServicesSelector.displayName = 'ProductsServicesSelector';

export default ProductsServicesSelector;
